"use client";

import { useState, useMemo } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { EquipmentCategory } from "@/types";
import { equipmentCategoriesData } from "@/data/pages/equipment";
import EquipmentItemCard from "./EquipmentItemCard";
import Container from "@/components/ui/Container";
import Section from "@/components/ui/Section";
import ScrollReveal from "@/components/ui/ScrollReveal";
import CategorySearchFilter from "@/components/ui/CategorySearchFilter";
import { FaArrowLeft } from "react-icons/fa6";

interface EquipmentCategoryDetailProps {
  category: EquipmentCategory;
}

export default function EquipmentCategoryDetail({
  category,
}: EquipmentCategoryDetailProps) {
  const router = useRouter();

  // Active folder selection (null means viewing root level with all items)
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>("");

  const categoryOptions = useMemo(() => {
    return equipmentCategoriesData.map((cat) => ({
      value: cat.slug,
      label: cat.shortTitle || cat.title,
    }));
  }, []);

  // Extract all subfolders at root level of this category
  const rootFolders = useMemo(() => {
    return category.items.filter((item) => item.type === "folder");
  }, [category.items]);

  // Active folder object if one is selected
  const activeFolder = useMemo(() => {
    if (!selectedFolderId) return null;
    return rootFolders.find((f) => f.id === selectedFolderId || f.slug === selectedFolderId) || null;
  }, [rootFolders, selectedFolderId]);

  // Filtered items based on search and selected folder state
  const displayedItems = useMemo(() => {
    if (activeFolder) {
      const items = activeFolder.items || [];
      if (!searchQuery.trim()) return items;
      return items.filter((item) =>
        item.title.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Root level view
    if (!searchQuery.trim()) return category.items;
    return category.items.filter((item) =>
      item.title.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [activeFolder, category.items, searchQuery]);

  return (
    <Section className="pt-32 sm:pt-40 lg:pt-48 pb-24 bg-black min-h-screen">
      <Container>
        {/* Back Link */}
        <ScrollReveal variant="fade-up" duration={600}>
          <div className="mb-6 flex items-center gap-3">
            {activeFolder ? (
              <button
                type="button"
                onClick={() => {
                  setSelectedFolderId(null);
                  setSearchQuery("");
                }}
                className="inline-flex items-center gap-2 font-poppins text-xs sm:text-sm text-white/60 hover:text-brand-cream transition-colors group cursor-pointer"
              >
                <FaArrowLeft className="w-3 h-3 transition-transform group-hover:-translate-x-1" />
                <span>Back to {category.shortTitle || category.title}</span>
              </button>
            ) : (
              <Link
                href="/capabilities#equipment"
                className="inline-flex items-center gap-2 font-poppins text-xs sm:text-sm text-white/60 hover:text-brand-cream transition-colors group"
              >
                <FaArrowLeft className="w-3 h-3 transition-transform group-hover:-translate-x-1" />
                <span>Back</span>
              </Link>
            )}
          </div>
        </ScrollReveal>

        {/* Top Header Bar */}
        <ScrollReveal variant="fade-up" duration={700} className="relative z-30">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-5 mb-8 sm:mb-10">
            {/* Left: Main Category Title */}
            <div className="relative group/title min-w-0 flex-1 mr-4">
              <h1
                title={category.title}
                className="font-poppins text-2xl sm:text-[26px] lg:text-[32px] font-regular text-white tracking-tight truncate leading-[1.2]"
              >
                {category.shortTitle || category.title}
              </h1>

              {/* Styled Tooltip */}
              <div className="absolute top-full left-0 mt-2 hidden sm:group-hover/title:block z-30 pointer-events-none transition-opacity duration-200">
                <div className="bg-[#121314] text-white text-xs font-poppins px-3 py-1.5 rounded-[10px] border border-white/15 shadow-xl whitespace-normal max-w-sm leading-relaxed">
                  {category.title}
                </div>
              </div>
            </div>

            {/* Right: Switch Category Dropdown + Search Input */}
            <CategorySearchFilter
              options={categoryOptions}
              selectedValue={category.slug}
              onCategoryChange={(slug) => {
                setSelectedFolderId(null);
                setSearchQuery("");
                router.push(`/capabilities/equipment/${slug}`);
              }}
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              placeholder="Categories"
              searchPlaceholder="Search"
              dropdownAriaLabel="Switch equipment category"
            />
          </div>
        </ScrollReveal>

        {/* STATE A: Subfolder Active */}
        {activeFolder ? (
          <div className="space-y-8 sm:space-y-10">
            {/* Top Row: Sibling Subfolders Cards Row */}
            {rootFolders.length > 0 && (
              <ScrollReveal variant="fade-up" duration={700}>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-4">
                  {rootFolders.map((folder) => {
                    const isSelected = folder.id === activeFolder.id;
                    return (
                      <EquipmentItemCard
                        key={folder.id}
                        item={folder}
                        isSelected={isSelected}
                        onSelectFolder={(f) => {
                          setSelectedFolderId(f.id);
                          setSearchQuery("");
                        }}
                        compact
                      />
                    );
                  })}
                </div>
              </ScrollReveal>
            )}

            {/* Bottom Section: Active Subfolder Content Container */}
            <ScrollReveal variant="fade-up" delay={150} duration={800}>
              <div className="bg-[#414141] border border-white/10 rounded-[10px] p-6 sm:p-8 md:p-10 shadow-2xl">
                {/* Active Subfolder Title */}
                <div className="mb-2 sm:mb-2 pb-2 flex items-center justify-between">
                  <h3 className="font-poppins text-xl sm:text-2xl font-semibold text-white">
                    {activeFolder.title}
                  </h3>
                </div>

                {/* Subfolder Items Grid */}
                {displayedItems.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
                    {displayedItems.map((subItem) => (
                      <EquipmentItemCard
                        key={subItem.id}
                        item={subItem}
                        onSelectFolder={(deeperFolder) => {
                          setSelectedFolderId(deeperFolder.id);
                          setSearchQuery("");
                        }}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-16 px-4">
                    <p className="font-poppins text-white/60 text-sm">
                      No documents found in {activeFolder.title}
                      {searchQuery ? ` matching "${searchQuery}"` : ""}.
                    </p>
                  </div>
                )}
              </div>
            </ScrollReveal>
          </div>
        ) : (
          /* STATE B: Root Level View */
          <ScrollReveal variant="fade-up" delay={100} duration={700}>
            {displayedItems.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
                {displayedItems.map((item) => (
                  <EquipmentItemCard
                    key={item.id}
                    item={item}
                    onSelectFolder={(folder) => {
                      setSelectedFolderId(folder.id);
                      setSearchQuery("");
                    }}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-20 px-4 bg-[#414141] border border-white/10 rounded-[10px]">
                <p className="font-poppins text-white/60 text-base mb-3">
                  No equipment items found{searchQuery ? ` matching "${searchQuery}"` : ""}.
                </p>
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery("")}
                    className="font-poppins text-xs sm:text-sm text-brand-green hover:underline"
                  >
                    Clear search query
                  </button>
                )}
              </div>
            )}
          </ScrollReveal>
        )}
      </Container>
    </Section>
  );
}
